import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Mic, Sparkles, Headphones, ShieldCheck, ArrowRight, Square, Volume2, Loader2, Play } from "lucide-react";

/**
 * WhoGuidesDemo — v2
 * Two modes:
 *  - "showcase" (default cycling animation) — no browser mic
 *  - "live" — user taps mic, browser Web Speech API records, transcript is sent to
 *    /api/who-guides/respond for a real Claude-generated Guide reply, then
 *    /api/who-guides/speak returns TTS audio that plays back in the browser.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROMPTS = [
  {
    utterance: "WHO, I just lost my job.",
    reply:
      "I hear you — that's a lot to carry at once. In the WHO pilot, a Guide would walk with you toward workforce and benefits resources, and stay with you until a real connection is confirmed. This demo can't take action, but you can explore verified resources in our Discovery directory.",
    followups: ["How would a Guide help?", "Explore Discovery", "What benefits could apply?"],
  },
  {
    utterance: "WHO, my landlord is starting an eviction.",
    reply:
      "You're not alone in this. In WHO's planned approach, a Guide would help you find emergency housing and legal aid resources — with your permission — and follow up so nothing falls through the cracks. In an emergency, please reach out to local services directly right away.",
    followups: ["How does follow-through work?", "Find housing resources", "What is a warm handoff?"],
  },
  {
    utterance: "WHO, I need to see a doctor but don't know where to start.",
    reply:
      "Let's take care of that first step. WHO's Discovery directory lists veteran-serving clinics and health resources you can contact directly. In the future pilot, a Guide would help coordinate that connection and confirm you actually got care.",
    followups: ["Explore health resources", "How is a connection confirmed?", "Talk about the pilot"],
  },
  {
    utterance: "WHO, I'm a veteran interested in starting a business in Pittsburgh. Where can I find help with a business plan, financing, and veteran-owned certification?",
    reply:
      "Great goal — Pittsburgh has a strong network for veteran entrepreneurs. In our Discovery directory you'll find resources like the SBA's veteran business programs, the Veterans Business Outreach Center serving Pennsylvania, SCORE Pittsburgh mentoring, and certification guidance. In the future, a WHO Guide would help coordinate across several of these at once — with your permission.",
    followups: ["Explore entrepreneurship resources", "What is certification?", "How would coordination work?"],
  },
  {
    utterance: "WHO, I'm struggling and I don't know where to begin.",
    reply:
      "Thank you for telling me — that takes courage. If things feel urgent or unsafe, please call or text 988 right now; veterans can press 1 for the Veterans Crisis Line. When you're ready, WHO's Discovery directory is a calm place to start exploring support options.",
    followups: ["Call or text 988", "Explore resources", "Just walk me through it"],
  },
];

const useTypewriter = (text, speed = 28, active = true) => {
  const [out, setOut] = useState("");
  useEffect(() => {
    if (!active) return;
    setOut("");
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setOut(text.slice(0, i));
      if (i >= text.length) clearInterval(t);
    }, speed);
    return () => clearInterval(t);
  }, [text, speed, active]);
  return out;
};

// Detect browser SpeechRecognition support
const SpeechRecognitionCtor =
  typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

export default function WhoGuidesDemo({ variant = "teaser", defaultMode = "showcase" }) {
  const [mode, setMode] = useState(defaultMode); // showcase | live
  const supportsLive = !!SpeechRecognitionCtor;

  return (
    <div className={`relative w-full ${variant === "teaser" ? "max-w-[560px]" : "max-w-[680px]"} mx-auto`} data-testid="who-guides-demo">
      <div className="absolute -inset-6 bg-gradient-to-br from-gold/25 via-transparent to-gold/10 blur-2xl rounded-[36px] pointer-events-none" aria-hidden />

      <div className="relative rounded-[28px] overflow-hidden bg-navy-deep border border-white/10 shadow-2xl">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-navy-900/80 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gold/15 border border-gold/50 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-gold" strokeWidth={2.4} />
            </div>
            <div className="leading-tight">
              <p className="text-cream font-display font-bold text-sm">WHO Guides</p>
              <p className="text-[10px] uppercase tracking-widest text-gold-light/80">Interactive demonstration · Not operational</p>
            </div>
          </div>
          {supportsLive && (
            <div className="flex items-center gap-1 rounded-full bg-white/[0.06] border border-white/10 p-1">
              <button
                type="button"
                onClick={() => setMode("showcase")}
                className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full transition-colors ${mode === "showcase" ? "bg-gold text-navy" : "text-cream/60 hover:text-cream"}`}
                data-testid="who-guides-mode-showcase"
              >
                Demo
              </button>
              <button
                type="button"
                onClick={() => setMode("live")}
                className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full transition-colors ${mode === "live" ? "bg-gold text-navy" : "text-cream/60 hover:text-cream"}`}
                data-testid="who-guides-mode-live"
              >
                Try it live
              </button>
            </div>
          )}
        </div>

        {/* Required demonstration disclosures */}
        <div className="px-6 py-3 bg-navy-900/60 border-b border-white/10 text-[11px] leading-relaxed text-cream/70" data-testid="voice-demo-disclosure">
          <p>
            <span className="font-semibold text-gold-light">Demonstration only.</span> Responses are AI-generated and the voice is synthetic — this is not an operational WHO Guide, and no human is monitoring this conversation. No referrals, cases, or provider notifications are created. Please don&rsquo;t share personal, medical, or case details; conversation content is not retained.
          </p>
          <p className="mt-1">
            <span className="font-semibold text-cream">In an emergency</span>, call 911. Veterans in crisis: call or text <span className="font-semibold text-cream">988</span>, then press 1.
          </p>
        </div>

        {mode === "showcase" ? (
          <ShowcaseBody variant={variant} />
        ) : (
          <LiveBody variant={variant} />
        )}
      </div>
    </div>
  );
}

/* -------------------------- SHOWCASE (cycling demo) -------------------------- */
function ShowcaseBody({ variant }) {
  const compact = variant === "teaser";
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState("listening");
  const cur = PROMPTS[i];
  const typed = useTypewriter(cur.utterance, 32, phase !== "listening");
  const replyTyped = useTypewriter(cur.reply, 14, phase === "replying" || phase === "idle");
  const bars = useMemo(() => Array.from({ length: compact ? 22 : 32 }, () => Math.random()), [compact]);

  useEffect(() => {
    setPhase("listening");
    const t = setTimeout(() => setPhase("transcribing"), 1500);
    return () => clearTimeout(t);
  }, [i]);
  useEffect(() => {
    if (phase !== "transcribing") return;
    const t = setTimeout(() => setPhase("replying"), cur.utterance.length * 32 + 350);
    return () => clearTimeout(t);
  }, [phase, cur.utterance]);
  useEffect(() => {
    if (phase !== "replying") return;
    const t = setTimeout(() => setPhase("idle"), cur.reply.length * 14 + 900);
    return () => clearTimeout(t);
  }, [phase, cur.reply]);
  useEffect(() => {
    if (phase !== "idle") return;
    const t = setTimeout(() => setI((v) => (v + 1) % PROMPTS.length), 3400);
    return () => clearTimeout(t);
  }, [phase]);

  return (
    <>
      <div className={`${compact ? "px-6 pt-7 pb-6" : "px-8 pt-9 pb-8"} bg-gradient-to-b from-navy-deep to-[#0A1524]`}>
        <p className="text-[11px] uppercase tracking-[0.22em] text-gold-light/80 font-semibold">How can we help today?</p>
        <div className={`mt-5 flex items-center gap-5 ${compact ? "" : "gap-6"}`}>
          <div className="relative shrink-0">
            <motion.div
              className="absolute inset-0 rounded-full bg-gold/40"
              animate={{ scale: phase === "listening" ? [1, 1.6, 1] : 1, opacity: phase === "listening" ? [0.6, 0, 0.6] : 0 }}
              transition={{ duration: 1.4, repeat: phase === "listening" ? Infinity : 0, ease: "easeOut" }}
              aria-hidden
            />
            <div className={`relative ${compact ? "w-14 h-14" : "w-16 h-16"} rounded-full bg-gold text-navy flex items-center justify-center shadow-[0_10px_30px_-5px_rgba(198,147,76,0.55)]`}>
              <Mic className={`${compact ? "w-6 h-6" : "w-7 h-7"}`} strokeWidth={2.4} />
            </div>
          </div>
          <div className="flex-1 flex items-center gap-[3px] h-12">
            {bars.map((b, idx) => (
              <motion.span
                key={idx}
                className="w-[3px] rounded-full bg-gradient-to-t from-gold-light/40 to-gold"
                animate={{
                  height: phase === "listening" || phase === "transcribing" ? [`${8 + b * 8}px`, `${20 + b * 26}px`, `${8 + b * 8}px`] : `${6 + b * 6}px`,
                  opacity: phase === "listening" || phase === "transcribing" ? [0.5, 1, 0.5] : 0.35,
                }}
                transition={{ duration: 0.8 + b * 0.6, repeat: phase === "listening" || phase === "transcribing" ? Infinity : 0, ease: "easeInOut", delay: idx * 0.03 }}
              />
            ))}
          </div>
        </div>

        <div className="mt-6">
          <p className="text-[10px] uppercase tracking-[0.22em] text-cream/45 font-semibold">You</p>
          <p className={`mt-1.5 font-display font-bold text-cream leading-snug ${compact ? "text-xl" : "text-2xl"}`} data-testid="who-guides-transcript">
            {phase === "listening" ? <span className="text-cream/50 italic">listening…</span> : <>{typed}{phase === "transcribing" && <span className="inline-block w-[2px] h-5 bg-gold ml-1 align-middle animate-pulse" />}</>}
          </p>
        </div>

        <div className={`${compact ? "mt-6" : "mt-7"} rounded-2xl border border-gold/25 bg-gradient-to-br from-navy-900/70 to-navy-900/30 backdrop-blur p-5`}>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
            <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">WHO Guide · Maya</p>
          </div>
          <p className={`mt-2 text-cream/90 leading-relaxed ${compact ? "text-[15px]" : "text-base"}`} data-testid="who-guides-reply">
            {phase === "replying" || phase === "idle" ? <>{replyTyped}{phase === "replying" && <span className="inline-block w-[2px] h-4 bg-gold-light ml-1 align-middle animate-pulse" />}</> : <span className="text-cream/30">Maya is thinking with you…</span>}
          </p>
          <AnimatePresence>
            {phase === "idle" && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="mt-4 flex flex-wrap gap-2">
                {cur.followups.map((f, idx) => (
                  <motion.button key={f} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08, duration: 0.35 }} className="text-xs font-semibold px-3.5 py-2 rounded-full bg-gold/12 hover:bg-gold/25 text-gold border border-gold/40 transition-colors" data-testid={`who-guides-chip-${idx}`} type="button">
                    {f}
                  </motion.button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {!compact && (
          <div className="mt-6 grid grid-cols-3 gap-3 text-[11px]">
            <MetaTile icon={<Headphones className="w-3.5 h-3.5" />} label="Voice-first" />
            <MetaTile icon={<ShieldCheck className="w-3.5 h-3.5" />} label="Consent-based" />
            <MetaTile icon={<ArrowRight className="w-3.5 h-3.5" />} label="Warm handoff" />
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-1.5 py-4 bg-navy-deep border-t border-white/5" data-testid="who-guides-progress">
        {PROMPTS.map((_, idx) => (
          <span key={idx} className={`h-1 rounded-full transition-all duration-500 ${idx === i ? "w-6 bg-gold" : "w-1.5 bg-white/15"}`} />
        ))}
      </div>
    </>
  );
}

/* -------------------------- LIVE (Web Speech + LLM + TTS) -------------------------- */
function LiveBody({ variant }) {
  const compact = variant === "teaser";
  const [state, setState] = useState("idle"); // idle | listening | thinking | speaking | error
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState("");
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const bars = useMemo(() => Array.from({ length: compact ? 22 : 32 }, () => Math.random()), [compact]);

  const startListening = () => {
    setError("");
    setReply("");
    setTranscript("");
    try {
      const rec = new SpeechRecognitionCtor();
      rec.lang = "en-US";
      rec.interimResults = true;
      rec.continuous = false;
      recognitionRef.current = rec;

      let final = "";
      rec.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i];
          if (r.isFinal) final += r[0].transcript;
          else interim += r[0].transcript;
        }
        setTranscript((final + interim).trim());
      };
      rec.onerror = (e) => {
        setError(e.error === "not-allowed" ? "Mic access denied. Enable microphone in browser settings." : `Voice error: ${e.error}`);
        setState("error");
      };
      rec.onend = () => {
        const spoken = (final || transcript).trim();
        if (spoken.length > 1) {
          respond(spoken);
        } else {
          setState("idle");
        }
      };
      rec.start();
      setState("listening");
    } catch (e) {
      setError("Voice recognition not available in this browser. Try Chrome or Edge.");
      setState("error");
    }
  };

  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch (_) { /* noop */ }
  };

  const respond = async (spoken) => {
    setState("thinking");
    setTranscript(spoken);
    try {
      const { data } = await axios.post(`${API}/who-guides/respond`, {
        transcript: spoken,
        session_id: sessionId || undefined,
      });
      setSessionId(data.session_id);
      setReply(data.reply);
      // Fire TTS
      setState("speaking");
      try {
        const audioRes = await axios.post(`${API}/who-guides/speak`, { text: data.reply, voice: "shimmer" }, { responseType: "blob" });
        const url = URL.createObjectURL(audioRes.data);
        if (audioRef.current) {
          audioRef.current.src = url;
          await audioRef.current.play().catch(() => {});
          audioRef.current.onended = () => setState("idle");
        } else {
          setState("idle");
        }
      } catch (_) {
        setState("idle"); // TTS optional — text still shown
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "The Guide is momentarily offline.");
      setState("error");
    }
  };

  const replay = async () => {
    if (audioRef.current?.src) {
      try {
        setState("speaking");
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
      } catch (_) {
        setState("idle");
      }
    }
  };

  return (
    <div className={`${compact ? "px-6 pt-7 pb-6" : "px-8 pt-9 pb-8"} bg-gradient-to-b from-navy-deep to-[#0A1524]`}>
      <p className="text-[11px] uppercase tracking-[0.22em] text-gold-light/80 font-semibold">How can we help today? · Live</p>

      <div className="mt-5 flex items-center gap-5">
        <div className="relative shrink-0">
          <motion.div
            className="absolute inset-0 rounded-full bg-gold/40"
            animate={{ scale: state === "listening" ? [1, 1.6, 1] : 1, opacity: state === "listening" ? [0.6, 0, 0.6] : 0 }}
            transition={{ duration: 1.4, repeat: state === "listening" ? Infinity : 0, ease: "easeOut" }}
            aria-hidden
          />
          <button
            type="button"
            onClick={state === "listening" ? stopListening : startListening}
            disabled={state === "thinking" || state === "speaking"}
            data-testid="who-guides-live-mic-btn"
            aria-label={state === "listening" ? "Stop recording" : "Start recording"}
            className={`relative ${compact ? "w-14 h-14" : "w-16 h-16"} rounded-full ${state === "listening" ? "bg-red-500 text-white" : "bg-gold text-navy"} flex items-center justify-center shadow-[0_10px_30px_-5px_rgba(198,147,76,0.55)] hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed transition-all`}
          >
            {state === "listening" ? <Square className="w-5 h-5" fill="currentColor" /> :
             state === "thinking" ? <Loader2 className="w-6 h-6 animate-spin" /> :
             state === "speaking" ? <Volume2 className="w-6 h-6 animate-pulse" /> :
             <Mic className={`${compact ? "w-6 h-6" : "w-7 h-7"}`} strokeWidth={2.4} />}
          </button>
        </div>

        <div className="flex-1 flex items-center gap-[3px] h-12">
          {bars.map((b, idx) => (
            <motion.span
              key={idx}
              className="w-[3px] rounded-full bg-gradient-to-t from-gold-light/40 to-gold"
              animate={{
                height: state === "listening" || state === "thinking" || state === "speaking" ? [`${8 + b * 8}px`, `${20 + b * 26}px`, `${8 + b * 8}px`] : `${6 + b * 6}px`,
                opacity: state === "listening" || state === "thinking" || state === "speaking" ? [0.5, 1, 0.5] : 0.25,
              }}
              transition={{ duration: 0.8 + b * 0.6, repeat: state === "listening" || state === "thinking" || state === "speaking" ? Infinity : 0, ease: "easeInOut", delay: idx * 0.03 }}
            />
          ))}
        </div>
      </div>

      <p className="mt-5 text-[10px] uppercase tracking-[0.22em] text-cream/45 font-semibold">
        {state === "idle" && !transcript && "Tap the mic to speak."}
        {state === "idle" && transcript && "You said"}
        {state === "listening" && "Listening… speak naturally, then tap the square to stop."}
        {state === "thinking" && "Maya is thinking with you…"}
        {state === "speaking" && "Maya is speaking…"}
        {state === "error" && "Something went wrong"}
      </p>

      <p className={`mt-1.5 font-display font-bold text-cream leading-snug ${compact ? "text-lg" : "text-xl"} min-h-[1.5em]`} data-testid="who-guides-live-transcript">
        {transcript || (state === "idle" ? <span className="text-cream/40 italic">Try: &ldquo;WHO, I just lost my job.&rdquo;</span> : "…")}
      </p>

      <div className={`${compact ? "mt-5" : "mt-6"} rounded-2xl border border-gold/25 bg-gradient-to-br from-navy-900/70 to-navy-900/30 backdrop-blur p-5 min-h-[100px]`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full bg-gold ${state === "speaking" || state === "thinking" ? "animate-pulse" : ""}`} />
            <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">WHO Guide · Maya</p>
          </div>
          {reply && (
            <button
              type="button"
              onClick={replay}
              className="text-[10px] uppercase tracking-widest font-bold text-gold-light hover:text-gold flex items-center gap-1"
              data-testid="who-guides-live-replay"
            >
              <Play className="w-3 h-3" /> Replay
            </button>
          )}
        </div>
        <p className={`mt-2 text-cream/90 leading-relaxed ${compact ? "text-[15px]" : "text-base"}`} data-testid="who-guides-live-reply">
          {reply || <span className="text-cream/30">{state === "thinking" ? "…" : "The Guide's response will appear here."}</span>}
        </p>
      </div>

      {error && (
        <p className="mt-3 text-xs text-red-300 bg-red-900/20 border border-red-500/40 rounded-md p-2" data-testid="who-guides-live-error">
          {error}
        </p>
      )}

      <p className="mt-4 text-[10px] text-cream/40 leading-relaxed">
        Live demo · Powered by Claude Sonnet 4.5 + OpenAI TTS via Emergent LLM. In-browser voice recognition. No audio is stored.
      </p>

      <audio ref={audioRef} className="hidden" />
    </div>
  );
}

function MetaTile({ icon, label }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-2 rounded-md bg-white/[0.03] border border-white/10 text-cream/80">
      <span className="text-gold">{icon}</span>
      <span className="font-semibold uppercase tracking-widest">{label}</span>
    </div>
  );
}
